import { Box, Text, useInput } from '../ink.js';
import type React from 'react';
import { theme } from '../theme.js';
import type { GoalSummary } from '../app-state.js';

export interface GoalPanelProps {
  goal: GoalSummary;
  /** Start the AutonomousCoordinator with the given goal text. */
  onCoordinatorStart?: ((goal: string) => void) | undefined;
  /** Stop the AutonomousCoordinator. */
  onCoordinatorStop?: (() => void) | undefined;
  /** Whether the coordinator is currently running. */
  coordinatorRunning?: boolean | undefined;
}

/**
 * Full-screen overlay showing the current goal, deliverables checklist,
 * and progress bar. Opened with F9.
 */
export function GoalPanel({
  goal,
  onCoordinatorStart,
  onCoordinatorStop,
  coordinatorRunning,
}: GoalPanelProps): React.ReactElement {
  // Keyboard shortcuts for coordinator control (called before early returns so hooks always fire)
  useInput((input) => {
    if (input === 'c' || input === 'C') {
      if (onCoordinatorStart && goal) {
        const goalText = goal.refinedGoal || goal.goal;
        onCoordinatorStart(goalText);
      }
    }
    if (input === 'S') {
      if (onCoordinatorStop) {
        onCoordinatorStop();
      }
    }
  });

  if (!goal) {
    return (
      <Box flexDirection="column" padding={1}>
        <Box marginBottom={1}>
          <Text bold color={theme.accent}>🎯 Goal</Text>
        </Box>
        <Box>
          <Text dimColor>No goal set. Use </Text>
          <Text color={theme.accent}>/goal set {"<mission>"}</Text>
          <Text dimColor> to create one.</Text>
        </Box>
        <Box marginTop={1}>
          <Text dimColor>Press F9 to close.</Text>
        </Box>
        {/* Coordinator controls — also work when no goal is set */}
        <Box marginTop={1}>
          {coordinatorRunning ? (
            <>
              <Text color="green">● Coordinator running  </Text>
              <Text dimColor>[S] Stop coordinator</Text>
            </>
          ) : (
            <Text dimColor>[C] Start coordinator</Text>
          )}
        </Box>
      </Box>
    );
  }

  const displayGoal = goal.refinedGoal || goal.goal;
  const stateIcon =
    goal.goalState === 'active' ? '🔄'
    : goal.goalState === 'paused' ? '⏸'
    : goal.goalState === 'completed' ? '✅'
    : '⏹';

  const stateColor =
    goal.goalState === 'active' ? 'green'
    : goal.goalState === 'paused' ? 'yellow'
    : goal.goalState === 'completed' ? 'green'
    : 'red';

  return (
    <Box flexDirection="column" padding={1}>
      {/* Header */}
      <Box marginBottom={1}>
        <Text bold color={theme.accent}>🎯 Goal — {goal.goalState}</Text>
      </Box>

      {/* Goal text */}
      <Box marginBottom={1}>
        <Text>{stateIcon} </Text>
        <Text bold>{displayGoal}</Text>
      </Box>

      {/* Original if refined */}
      {goal.refinedGoal && goal.refinedGoal !== goal.goal && (
        <Box marginBottom={1}>
          <Text dimColor>  (original: {goal.goal.length > 60 ? goal.goal.slice(0, 57) + '…' : goal.goal})</Text>
        </Box>
      )}

      {/* Progress bar */}
      {typeof goal.progress === 'number' && (
        <Box flexDirection="column" marginBottom={1}>
          <Box>
            {renderProgressBar(goal.progress, goal.progressTrend)}
          </Box>
          {goal.progressNote && (
            <Box>
              <Text dimColor>  {goal.progressNote}</Text>
            </Box>
          )}
        </Box>
      )}

      {/* Deliverables checklist */}
      {goal.deliverables && goal.deliverables.length > 0 && (
        <Box flexDirection="column" marginBottom={1}>
          <Box>
            <Text bold>Deliverables ({goal.deliverables.length}):</Text>
          </Box>
          {goal.deliverables.map((d, i) => {
            const done = /^\[[x✓]\]|✅|\(done\)/i.test(d);
            return (
              <Box key={i}>
                <Text {...(done ? { color: 'green' } : {})} dimColor={!done}>
                  {'  '}{done ? '✓' : '○'} {d}
                </Text>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Stats */}
      <Box flexDirection="column" marginBottom={1}>
        <Box>
          <Text dimColor>Iterations: </Text>
          <Text>{goal.iterations}</Text>
        </Box>
        <Box>
          <Text dimColor>State: </Text>
          <Text color={stateColor}>{stateIcon} {goal.goalState}</Text>
        </Box>
        {goal.lastTask && (
          <Box>
            <Text dimColor>Last task: </Text>
            <Text>{goal.lastTask.length > 50 ? goal.lastTask.slice(0, 47) + '…' : goal.lastTask}</Text>
          </Box>
        )}
        <Box marginTop={1}>
          <Text dimColor>Press F9 to close.</Text>
        </Box>
        {/* Coordinator status + controls */}
        <Box marginTop={1}>
          {coordinatorRunning ? (
            <>
              <Text color="green">● Coordinator running  </Text>
              <Text dimColor>[S] Stop coordinator</Text>
            </>
          ) : (
            <Text dimColor>[C] Start coordinator</Text>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function renderProgressBar(progress: number, trend?: string): React.ReactElement {
  const pct = Math.min(100, Math.max(0, Math.round(progress)));
  const filled = Math.round(pct / 5);
  const empty = 20 - filled;

  const trendIcon =
    trend === 'accelerating' ? ' 🚀'
    : trend === 'stalling' ? ' ⚠️'
    : trend === 'steady' ? ' ➡️'
    : '';

  return (
    <Box>
      <Text bold>Progress: </Text>
      <Text color="green">{'█'.repeat(filled)}</Text>
      <Text dimColor>{'░'.repeat(empty)}</Text>
      <Text bold> {pct}%</Text>
      {trend && <Text dimColor>{trendIcon} {trend}</Text>}
    </Box>
  );
}
